/**
 * "(Kilde N)" citation markers — frontend side.
 *
 * The model marks the sources it used with "(Kilde N)" in many shapes. The
 * markers are bookkeeping for the backend (which citations an answer used);
 * parents see the sources behind "Vis kilder" instead, so every marker is
 * removed from the displayed answer — old and new messages alike. Stored
 * content keeps its markers.
 *
 * Mirrors coord-backend/src/lib/citationMarkers.js and accepts the same
 * shapes (whole bracketed group):
 *   (Kilde 1)  (kilde 1)  (KILDE 1)   single, any case
 *   (Kilder 1 og 2)                    plural, "og" list
 *   (Kilde 1, 2, 3)                    comma list
 *   (Kilde 5, Kilde 6)                 repeated prefix
 *   (Kilde 1; Kilde 2)                 semicolon list
 *   (Kilde 1–3)                        range (en/em dash or hyphen)
 *   (Kilde: 1)  (Kilder: 1 og 2)       colon after Kilde/Kilder
 *   (Kilde nr. 1)  (Kilder nr 1, 2)    "nr." (dot optional)
 *   [Kilde 1]                          square brackets (not a markdown link)
 *   (se Kilde 1)                       "se" prefix
 *   NBSP / narrow NBSP inside any of the above
 * Left alone: "(se nav.no)", "(2 kilder)", "(kapittel 6)" and a bare
 * "Kilde 1" in running text.
 *
 * Runs on the stored answer HTML (markdown-it output), so besides the raw
 * characters it also accepts the HTML spellings of NBSP and "&".
 */

// Whitespace, including NBSP / narrow NBSP as characters (\s) or entities.
const SPACE = '(?:\\s|&nbsp;|&#0*160;|&#x0*a0;|&#0*8239;|&#x0*202f;)';
// Horizontal whitespace only — a stripped marker never takes a line break.
const HSPACE = '(?:[ \\t\\u00a0\\u202f]|&nbsp;|&#0*160;|&#x0*a0;|&#0*8239;|&#x0*202f;)';
const NUMBER = '\\d{1,3}';
// "Kilde" / "Kilder", optionally followed by ":" or "nr." ("Kilde nr. 1").
const KILDE = `kilder?(?:${SPACE}*:|${SPACE}+nr\\.?)?`;
// Between numbers: comma, semicolon, ampersand, "og", or a range dash.
const SEPARATOR = `${SPACE}*(?:[,;]|&amp;|&|\\bog\\b|[-–—])${SPACE}*`;
const MARKER_BODY = `(?:se${SPACE}+)?${KILDE}${SPACE}*${NUMBER}(?:${SEPARATOR}(?:${KILDE}${SPACE}*)?${NUMBER})*`;
// Square brackets not followed by "(" — "[Kilde 1](url)" is a markdown link.
const MARKER = `(?:\\(${SPACE}*${MARKER_BODY}${SPACE}*\\)|\\[${SPACE}*${MARKER_BODY}${SPACE}*\\](?!\\())`;
const MARKER_RUN = `${MARKER}(?:${HSPACE}*${MARKER})*`;

// A paragraph holding nothing but markers goes entirely.
const MARKER_ONLY_PARAGRAPH = new RegExp(`<p>${HSPACE}*${MARKER_RUN}${HSPACE}*</p>\\n?`, 'gi');
// Markers opening a line or block take the space after them along:
// "<p>(Kilde 1) Tekst" → "<p>Tekst".
const LINE_START_MARKERS = new RegExp(
  `(^|\\n|<(?:p|li|td|th|h[1-6]|blockquote|div)(?:\\s[^>]*)?>|<br\\s*/?>)${HSPACE}*${MARKER_RUN}${HSPACE}*`,
  'gi'
);
// Every other marker takes the space before it along:
// "rett på stønad (Kilde 1)." → "rett på stønad."
const MARKER_WITH_LEADING_SPACE = new RegExp(`(${HSPACE}*)${MARKER}`, 'gi');
const STARTS_WITH_WORD = /^[\p{L}\p{N}]/u;

/**
 * Removes every Kilde marker from an answer (HTML or plain text) without
 * leaving empty brackets or doubled spaces behind.
 *
 * @param {string} text
 * @returns {string}
 */
export const stripCitationMarkers = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(MARKER_ONLY_PARAGRAPH, '')
    .replace(LINE_START_MARKERS, '$1')
    .replace(MARKER_WITH_LEADING_SPACE, (match, before, offset, whole) =>
      // A marker glued to the next word keeps one space: "a (Kilde 1)b" → "a b".
      (before && STARTS_WITH_WORD.test(whole.slice(offset + match.length)) ? before : '')
    );
};
